/**
 * StandardsCompass
 * ----------------
 * Click the standards a job is built to — ANSI/AAMI ST108, ST79, ASHE ICRA,
 * ILSM, ASHRAE 170, NFPA 99/241, FGI — and the record carries them. Then paste
 * the questions that came in with the RFQ and AskIsaac answers them against
 * those standards, citing the clause it relied on. Prints as a compliance matrix.
 *
 * Reusable: <StandardsCompass linkType="project|bid_note|solicitation" linkId={id} label="…" token={t} />
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, Sparkles, Save, Copy, FileText, Download, Search,
  MessageSquareQuote, AlertTriangle, Trash2, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";

const CONF = {
  high: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-red-50 text-red-700 border-red-200",
};

const ICRA_CLASSES = ["", "I", "II", "III", "III/IV", "IV"];
const ACTIVITY_TYPES = ["", "A", "B", "C", "D"];
const RISK_GROUPS = ["", "1", "2", "3", "4"];

export const StandardsCompass = ({ linkType, linkId, label = "", token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [library, setLibrary] = useState([]);
  const [domains, setDomains] = useState([]);
  const [sel, setSel] = useState({ standard_ids: [], icra_class: "", activity_type: "",
    patient_risk_group: "", notes: "", reasons: {} });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [questions, setQuestions] = useState("");
  const [asking, setAsking] = useState(false);
  const [batches, setBatches] = useState([]);
  const [expanded, setExpanded] = useState({});

  const params = { link_type: linkType, link_id: linkId };

  const load = useCallback(async () => {
    try {
      const [lib, s, a] = await Promise.all([
        axios.get(`${API}/standards/library`, { headers }),
        axios.get(`${API}/standards/selection`, { headers, params }),
        axios.get(`${API}/standards/answers`, { headers, params }),
      ]);
      setLibrary(lib.data?.standards || []);
      setDomains(lib.data?.domains || []);
      const got = s.data?.selection || {};
      setSel({
        standard_ids: got.standard_ids || [], icra_class: got.icra_class || "",
        activity_type: got.activity_type || "", patient_risk_group: got.patient_risk_group || "",
        notes: got.notes || "", reasons: got.reasons || {},
      });
      setBatches(a.data?.batches || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the standards desk");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkType, linkId, token]);

  useEffect(() => { if (linkId) load(); }, [linkId, load]);

  const toggle = (id) => setSel((s) => ({
    ...s,
    standard_ids: s.standard_ids.includes(id)
      ? s.standard_ids.filter((x) => x !== id)
      : [...s.standard_ids, id],
  }));

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/standards/selection`, {
        link_type: linkType, link_id: linkId, label,
        standard_ids: sel.standard_ids, icra_class: sel.icra_class,
        activity_type: sel.activity_type, patient_risk_group: sel.patient_risk_group,
        notes: sel.notes, reasons: sel.reasons,
      }, { headers });
      toast.success(`${sel.standard_ids.length} standard(s) locked to this record`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the selection"); }
    finally { setBusy(false); }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const r = await axios.post(`${API}/standards/suggest`,
        { link_type: linkType, link_id: linkId }, { headers });
      setSuggested(r.data);
      if (!r.data.suggested?.length) toast.info("Nothing suggested — describe the project on the record first");
    } catch (e) { toast.error(e?.response?.data?.detail || "The suggestion failed"); }
    finally { setSuggesting(false); }
  };

  const acceptSuggestions = () => {
    if (!suggested?.suggested?.length) return;
    setSel((s) => {
      const reasons = { ...s.reasons };
      suggested.suggested.forEach((x) => { if (x.reason) reasons[x.id] = x.reason; });
      return {
        ...s,
        standard_ids: Array.from(new Set([...s.standard_ids, ...suggested.suggested.map((x) => x.id)])),
        reasons,
        notes: s.notes || suggested.notes || "",
      };
    });
    toast.success("Added to the selection — review, then Save");
  };

  const ask = async () => {
    if (!questions.trim()) { toast.error("Paste the questions that came in"); return; }
    if (!sel.standard_ids.length) { toast.error("Tick the standards this job follows first"); return; }
    setAsking(true);
    try {
      const r = await axios.post(`${API}/standards/answer`, {
        link_type: linkType, link_id: linkId, questions,
        standard_ids: sel.standard_ids,
      }, { headers });
      setBatches((b) => [r.data.batch, ...b]);
      setQuestions("");
      toast.success(`${r.data.batch.answers.length} answer(s) written against your standards`);
    } catch (e) { toast.error(e?.response?.data?.detail || "The answer engine failed"); }
    finally { setAsking(false); }
  };

  const copyBatch = async (b) => {
    const text = (b.answers || []).map((a, i) => {
      const cites = (a.standards_cited || []).map((c) => `${c.code}${c.clause ? ` — ${c.clause}` : ""}`).join("\n     ");
      return `${i + 1}. ${a.question}\n\n${a.answer}\n`
        + (a.our_position ? `\nOur position: ${a.our_position}\n` : "")
        + (a.owner_action_needed ? `\nWe need from you: ${a.owner_action_needed}\n` : "")
        + (cites ? `\nBasis:\n     ${cites}\n` : "")
        + (a.caveat ? `\nNote: ${a.caveat}\n` : "");
    }).join("\n----------------------------------------\n\n");
    await navigator.clipboard.writeText(text);
    toast.success("Answers copied — paste into your reply");
  };

  const removeBatch = async (b) => {
    if (!window.confirm("Delete this set of answers?")) return;
    try {
      await axios.delete(`${API}/standards/answers/${b.id}`, { headers });
      setBatches((x) => x.filter((y) => y.id !== b.id));
    } catch { toast.error("Could not delete it"); }
  };

  const needle = q.trim().toLowerCase();
  const byDomain = domains.map((d) => ({
    domain: d.domain,
    items: library.filter((s) => s.domain === d.domain && (!needle
      || [s.code, s.title, s.summary, (s.applies_to || []).join(" ")]
        .some((v) => (v || "").toLowerCase().includes(needle)))),
  })).filter((d) => d.items.length);

  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="standards-compass">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-black text-slate-900 inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-sky-600" /> Standards this work follows
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Tick the ANSI/AAMI, ICRA, ILSM, ASHRAE, NFPA and FGI standards that govern this job.
            Everything the answer engine says is written against what you tick here.
          </p>
          <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
            <span className="px-2 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200 font-bold"
              data-testid="standards-count">
              {sel.standard_ids.length} selected
            </span>
            {sel.icra_class && (
              <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200 font-bold">
                ICRA Class {sel.icra_class}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={suggest} disabled={suggesting}
            data-testid="standards-suggest">
            {suggesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Suggest from the record
          </Button>
          <button onClick={() => openAuthedDoc("/standards/compliance-sheet", { token, fmt: "html", params })}
            className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50"
            data-testid="standards-preview">
            <FileText className="w-3.5 h-3.5 mr-1" /> Matrix
          </button>
          <button onClick={() => openAuthedDoc("/standards/compliance-sheet", { token, fmt: "pdf", params })}
            className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50"
            data-testid="standards-pdf">
            <Download className="w-3.5 h-3.5 mr-1" /> PDF
          </button>
          <Button size="sm" onClick={save} disabled={busy} className="bg-sky-700 hover:bg-sky-800"
            data-testid="standards-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
        </div>
      </div>

      {suggested && (
        <div className="mb-3 rounded-md border border-sky-200 bg-sky-50/70 px-3 py-2.5 text-xs"
          data-testid="standards-suggestions">
          <div className="flex items-start justify-between gap-2">
            <p className="font-bold text-sky-900">AskIsaac suggests {suggested.suggested.length} standard(s)</p>
            <Button size="sm" variant="outline" onClick={acceptSuggestions} className="h-7"
              data-testid="standards-accept-suggestions">
              Add all to selection
            </Button>
          </div>
          <ul className="mt-1.5 space-y-1">
            {suggested.suggested.map((s) => (
              <li key={s.id} className="text-slate-700">
                <strong>{s.code}</strong> — {s.reason || s.title}
              </li>
            ))}
          </ul>
          {suggested.notes && <p className="mt-1.5 text-sky-900">{suggested.notes}</p>}
        </div>
      )}

      {/* ICRA quick set */}
      <div className="grid sm:grid-cols-3 gap-2 mb-3">
        {[["activity_type", "Construction activity type", ACTIVITY_TYPES],
          ["patient_risk_group", "Patient risk group", RISK_GROUPS],
          ["icra_class", "ICRA class of precautions", ICRA_CLASSES]].map(([k, lbl, opts]) => (
          <label key={k} className="text-[11px] text-slate-600">
            {lbl}
            <select value={sel[k]} onChange={(e) => setSel({ ...sel, [k]: e.target.value })}
              className="mt-0.5 h-9 w-full text-xs border border-slate-300 rounded-md px-2 bg-white"
              data-testid={`standards-${k}`}>
              {opts.map((o) => <option key={o} value={o}>{o || "— not set —"}</option>)}
            </select>
          </label>
        ))}
      </div>

      <div className="relative mb-2">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8 text-xs"
          placeholder="Search standards — water, endoscope, containment, ventilation…"
          data-testid="standards-search" />
      </div>

      <div className="space-y-3 max-h-[420px] overflow-auto pr-1">
        {byDomain.map((d) => (
          <div key={d.domain}>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1.5">{d.domain}</p>
            <div className="grid md:grid-cols-2 gap-2">
              {d.items.map((s) => {
                const on = sel.standard_ids.includes(s.id);
                const open = expanded[s.id];
                return (
                  <div key={s.id} className={`rounded-md border p-2.5 ${on
                    ? "border-sky-400 bg-sky-50/60" : "border-slate-200 bg-white"}`}>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={on} onChange={() => toggle(s.id)}
                        className="mt-0.5" data-testid={`standard-${s.id}`} />
                      <span className="min-w-0">
                        <span className="block text-[12px] font-bold text-slate-900">{s.code}</span>
                        <span className="block text-[11px] text-slate-700">{s.title}</span>
                        <span className="block text-[10.5px] text-slate-500 mt-0.5">{s.summary}</span>
                      </span>
                    </label>
                    {on && (sel.reasons || {})[s.id] && (
                      <p className="text-[10px] text-sky-800 italic mt-1 ml-6">Applies here: {sel.reasons[s.id]}</p>
                    )}
                    <button onClick={() => setExpanded((x) => ({ ...x, [s.id]: !open }))}
                      className="ml-6 mt-1 text-[10px] font-bold uppercase tracking-wide text-sky-700 hover:underline">
                      {open ? "Hide requirements" : `${s.requirements.length} requirements`}
                    </button>
                    {open && (
                      <ul className="ml-6 mt-1 space-y-0.5 list-disc pl-3">
                        {s.requirements.map((r, i) => (
                          <li key={i} className="text-[10.5px] text-slate-600">{r}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {!byDomain.length && <p className="text-sm text-slate-400">Nothing matches that search.</p>}
      </div>

      <Textarea value={sel.notes} onChange={(e) => setSel({ ...sel, notes: e.target.value })} rows={2}
        className="mt-3 text-xs" placeholder="Desk notes — AHJ positions, owner standards, manufacturer IFU limits…"
        data-testid="standards-notes" />

      {/* -------------------------------------------------- answer engine */}
      <div className="mt-5 pt-4 border-t border-slate-200">
        <h4 className="text-sm font-black text-slate-900 inline-flex items-center gap-2">
          <MessageSquareQuote className="w-4 h-4 text-sky-600" /> Answer the questions that came in
        </h4>
        <p className="text-xs text-slate-500 mt-0.5">
          Paste the owner's, agency's or GC's questions — one per line. Answers are written against
          the standards you ticked above, with the clause cited and an honest confidence.
        </p>
        <Textarea value={questions} onChange={(e) => setQuestions(e.target.value)} rows={4}
          className="mt-2 text-xs"
          placeholder={"What water quality will the final rinse meet?\nHow will you maintain negative pressure during demolition?\nWho verifies the sterilizer qualification after installation?"}
          data-testid="standards-questions" />
        <div className="flex justify-end mt-2">
          <Button size="sm" onClick={ask} disabled={asking} className="bg-sky-700 hover:bg-sky-800"
            data-testid="standards-ask">
            {asking ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Answer to standard
          </Button>
        </div>

        <div className="space-y-3 mt-3">
          {batches.map((b) => (
            <div key={b.id} className="rounded-lg border border-slate-200 p-3" data-testid={`standards-batch-${b.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="text-[11px] text-slate-500">
                  {(b.answers || []).length} answer(s) · {b.created_at ? new Date(b.created_at).toLocaleString() : ""}
                  {b.created_by ? ` · ${b.created_by}` : ""}
                </p>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7" onClick={() => copyBatch(b)}
                    data-testid={`standards-copy-${b.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-600 hover:bg-red-50"
                    onClick={() => removeBatch(b)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2.5">
                {(b.answers || []).map((a) => (
                  <div key={a.id} className="border-l-2 border-sky-300 pl-2.5">
                    <p className="text-[12.5px] font-bold text-slate-900">{a.question}</p>
                    <p className="text-[12px] text-slate-700 mt-0.5">{a.answer}</p>
                    {a.our_position && (
                      <p className="text-[11px] text-slate-700 mt-1">
                        <strong>Our position:</strong> {a.our_position}
                      </p>
                    )}
                    {a.owner_action_needed && (
                      <p className="text-[11px] text-indigo-800 mt-0.5">
                        <strong>We need from you:</strong> {a.owner_action_needed}
                      </p>
                    )}
                    {!!(a.standards_cited || []).length && (
                      <ul className="mt-1 space-y-0.5">
                        {a.standards_cited.map((c, i) => (
                          <li key={i} className="text-[10.5px] text-sky-800">
                            <strong>{c.code}</strong>{c.clause ? ` — ${c.clause}` : ""}
                            {c.why ? <span className="text-slate-500"> · {c.why}</span> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`text-[9px] font-black uppercase tracking-wider border rounded px-1.5 py-0.5 ${CONF[a.confidence] || CONF.medium}`}>
                        {a.confidence} confidence
                      </span>
                      {a.caveat && (
                        <span className="text-[10.5px] text-amber-800 inline-flex items-start gap-1">
                          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {a.caveat}
                        </span>
                      )}
                      {!!a.uncited_dropped && (
                        <span className="text-[10px] text-slate-400">
                          {a.uncited_dropped} unverifiable citation(s) removed
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!batches.length && (
            <p className="text-sm text-slate-400 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-slate-300" />
              No questions answered on this record yet.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default StandardsCompass;
